import { ColumnEditorOptions } from "primereact/column";
import { Calendar } from "primereact/calendar";
import { Nullable } from "primereact/ts-helpers";
import { useState } from "react";

interface DateEditorProps {
    options: ColumnEditorOptions;
}

// Parse DD/MM/YYYY format to Date object
const parseDate = (value: unknown): Date | null => {
    if (!value) return null;

    if (value instanceof Date) return value;

    if (typeof value === "string") {
        // Try DD/MM/YYYY format first
        const ddmmyyyy = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (ddmmyyyy) {
            const [, day, month, year] = ddmmyyyy;
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }

        // Try ISO format
        const date = new Date(value);
        if (!isNaN(date.getTime())) return date;
    }

    return null;
};

// Format Date to DD/MM/YYYY string
const formatDate = (date: Date | null): string => {
    if (!date) return "";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
};

export const DateEditor = ({ options }: DateEditorProps) => {
    // Seed once so the existing date shows on open and survives re-renders.
    const [date, setDate] = useState<Date | null>(() => parseDate(options.value));

    const handleChange = (value: Nullable<Date>) => {
        setDate(value ?? null);
        // Store as DD/MM/YYYY to match the mobile app.
        options.editorCallback?.(value ? formatDate(value) : null);
    };

    return (
        <Calendar
            value={date}
            onChange={(e) => handleChange(e.value)}
            dateFormat="dd/mm/yy"
            showIcon
            autoFocus
        />
    );
};

export const dateDisplayTemplate = (value: unknown): string => {
    if (!value) return "";

    const date = parseDate(value);
    if (!date) return String(value);

    return formatDate(date);
};
